import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Axios from "axios";
import { scrubSurveyImages } from "components/survey/formTheme";
import { useParams } from "react-router-dom";
import { Button, Card, Loader, Message, SubNav, ViewContext } from "components/lib";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import JSZip from "jszip";
import "survey-core/defaultV2.min.css";
import { applyStackgoTheme } from "components/survey/stackgo-theme";
import { attachScoring } from "./scoringRunner";
import { ScoringSummary, ScoreOverrideControl } from "./scoringSummary";

const Settings = require("settings.json");
const serverUrl = Settings[process.env.NODE_ENV].server_url;

StylesManager.applyTheme("defaultV2");

function buildSurveyModel(schema) {
  if (!schema || typeof schema !== "object") return null;
  const model = scrubSurveyImages(new Model(schema));
  applyStackgoTheme(model);
  model.showCompletedPage = false;
  return model;
}

function isFileLikeObject(value) {
  if (!value || typeof value !== "object") return false;
  return (
    (typeof value.name === "string" || typeof value.filename === "string") &&
    (typeof value.key === "string" ||
      typeof value.content === "string" ||
      typeof value.data === "string")
  );
}

function dataUrlToBase64(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  if (dataUrl.startsWith("data:")) {
    const commaIndex = dataUrl.indexOf(",");
    return commaIndex === -1 ? null : dataUrl.substring(commaIndex + 1);
  }

  if (/^[A-Za-z0-9+/=\n\r]+$/.test(dataUrl)) {
    return dataUrl.replace(/\s/g, "");
  }

  return null;
}

function collectDownloadableFiles(node, output = []) {
  if (!node) return output;

  if (Array.isArray(node)) {
    node.forEach((item) => collectDownloadableFiles(item, output));
    return output;
  }

  if (isFileLikeObject(node)) {
    output.push(node);
    return output;
  }

  if (typeof node === "object") {
    Object.values(node).forEach((value) => collectDownloadableFiles(value, output));
  }

  return output;
}

function buildNavItems(verificationId, verificationInfo) {
  const items = [
    {
      label: "KYC",
      link: `/verification/${verificationId}`,
    },
  ];

  if (verificationInfo?.sanctions_results) {
    items.push({
      label: "PEP/Sanctions",
      link: `/verification/sanctions/${verificationId}`,
    });
  }

  if (verificationInfo?.adverse_media_results) {
    items.push({
      label: "Adverse Media",
      link: `/verification/adverse/${verificationId}`,
    });
  }

  if (verificationInfo?.response_id) {
    items.push({
      label: "Response",
      link: `/verification/${verificationId}/response/${verificationInfo.response_id}`,
    });
  }

  items.push({
    label: "Risk Assessment",
    link: `/verification/risk-assessment/${verificationId}`,
  });

  return items;
}

function formatSubmittedAnswers(sender, uploadPrefix) {
  const output = {};
  Object.keys(sender?.data || {}).forEach((key) => {
    const question = sender.getQuestionByName(key);
    if (!question) return;

    if (question.getType() === "file" && Array.isArray(question.value)) {
      output[key] = question.value.map((fileItem) => ({
        ...fileItem,
        key: fileItem.key || `${uploadPrefix}/${key}/${fileItem.name}`,
      }));
      return;
    }

    output[key] = question.value;
  });

  return output;
}

export function RiskAssessmentView() {
  const params = useParams();
  const context = useContext(ViewContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [publicLinkError, setPublicLinkError] = useState(null);
  const [publicLinkData, setPublicLinkData] = useState(null);
  const [creatingPublicLink, setCreatingPublicLink] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [displayAnswers, setDisplayAnswers] = useState(null);
  const [displayFiles, setDisplayFiles] = useState([]);
  const [hydratingFiles, setHydratingFiles] = useState(false);
  const isSubmittingRef = useRef(false);

  const fetchRiskAssessment = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await Axios.get(`/api/risk-assessment/${params.id}`);
      setRiskData(response.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to load this risk assessment right now."
      );
      setRiskData(null);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  useEffect(() => {
    fetchRiskAssessment();
  }, [fetchRiskAssessment]);

  const riskAssessment = riskData?.risk_assessment || null;
  // The API returns `submission` only for a completed risk decision; treat that
  // (or the status string) as completed so a completed assessment renders
  // read-only with its answers even if `status` has drifted (e.g. EDD active).
  const isCompleted =
    !!riskData?.submission || riskAssessment?.status === "completed";
  const uploadPrefix = riskData?.risk_assessment?.id || params.id;
  const submissionAnswers =
    riskData?.submission?.answers &&
    typeof riskData.submission.answers === "object"
      ? riskData.submission.answers
      : null;
  const scoringSummary =
    riskData?.scoring || riskData?.risk_assessment?.scoring || null;
  const scoringRating =
    riskData?.rating || riskData?.risk_assessment?.rating || null;
  const scoringResponseId =
    riskData?.submission?.response_id ||
    riskData?.risk_assessment?.response_id ||
    null;

  useEffect(() => {
    let mounted = true;

    async function hydrateCompletedAnswers() {
      if (!isCompleted || !submissionAnswers) {
        if (mounted) {
          setDisplayAnswers(null);
          setDisplayFiles([]);
          setHydratingFiles(false);
          setFileLoadError(null);
        }
        return;
      }

      setHydratingFiles(true);
      setFileLoadError(null);

      const cache = new Map();
      let hadFileFailure = false;

      const hydrateNode = async (node) => {
        if (!node) return node;

        if (Array.isArray(node)) {
          const hydratedArray = [];
          for (const item of node) {
            hydratedArray.push(await hydrateNode(item));
          }
          return hydratedArray;
        }

        if (isFileLikeObject(node)) {
          if (!node.key) return node;

          if (cache.has(node.key)) {
            return {
              ...node,
              ...cache.get(node.key),
            };
          }

          try {
            const response = await Axios.get("/api/utility/gcp/download", {
              params: {
                content: node.key,
                name: node.name || node.filename || node.key,
              },
            });
            const hydratedFile = {
              content: response.data?.blob,
              url: response.data?.url,
            };
            cache.set(node.key, hydratedFile);
            return {
              ...node,
              ...hydratedFile,
            };
          } catch (err) {
            hadFileFailure = true;
            return node;
          }
        }

        if (typeof node === "object") {
          const hydratedObject = {};
          for (const [key, value] of Object.entries(node)) {
            hydratedObject[key] = await hydrateNode(value);
          }
          return hydratedObject;
        }

        return node;
      };

      try {
        const hydratedAnswers = await hydrateNode(submissionAnswers);
        if (!mounted) return;
        setDisplayAnswers(hydratedAnswers);
        setDisplayFiles(collectDownloadableFiles(hydratedAnswers, []));
        if (hadFileFailure) {
          setFileLoadError("Some file attachments could not be loaded.");
        }
      } catch (err) {
        if (!mounted) return;
        setDisplayAnswers(submissionAnswers);
        setDisplayFiles(collectDownloadableFiles(submissionAnswers, []));
        setFileLoadError("Some file attachments could not be loaded.");
      } finally {
        if (mounted) {
          setHydratingFiles(false);
        }
      }
    }

    hydrateCompletedAnswers();

    return () => {
      mounted = false;
    };
  }, [isCompleted, submissionAnswers]);

  const surveyModel = useMemo(
    () => {
      const model = buildSurveyModel(riskData?.form?.schema);
      if (!model) return null;

      if (isCompleted) {
        model.mode = "display";
        model.data = displayAnswers || submissionAnswers || {};
      }

      return model;
    },
    [riskData?.form?.schema, isCompleted, displayAnswers, submissionAnswers]
  );

  const uploadFiles = useCallback(
    (sender, options) => {
      const formData = new FormData();
      options.files.forEach((file) => {
        formData.append("file", file);
        formData.append("response_id", uploadPrefix);
        formData.append("field_name", options.name);
        formData.append("file_name", file.name);
      });

      Axios.post(`${serverUrl}/api/utility/gcp/upload`, formData)
        .then((response) => {
          options.callback(
            "success",
            options.files.map((file) => ({
              file,
              name: file.name,
              type: file.type || "application/octet-stream",
              content: response.data.url,
              key: response.data.name,
              id: file.id,
            }))
          );
        })
        .catch(() => {
          options.callback("error", "File upload failed");
        });
    },
    [uploadPrefix]
  );

  useEffect(() => {
    if (!surveyModel) return undefined;
    return attachScoring(surveyModel, riskData?.form?.schema);
  }, [surveyModel, riskData?.form?.schema]);

  useEffect(() => {
    if (!surveyModel || riskData?.can_submit !== true) return undefined;

    const completing = async (sender, options) => {
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;
      options.allowComplete = false;
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      try {
        const answers = formatSubmittedAnswers(sender, uploadPrefix);
        const response = await Axios.post(
          `/api/risk-assessment/${params.id}/complete`,
          answers
        );
        const completedAt =
          response?.data?.data?.completed_at || new Date().toISOString();

        setRiskData((current) => {
          if (!current) return current;
          return {
            ...current,
            can_submit: false,
            rating: response?.data?.data?.rating ?? current.rating ?? null,
            scoring: response?.data?.data?.scoring ?? current.scoring ?? null,
            submission: {
              form_id: current.form?.id || null,
              form_name: current.form?.name || null,
              form_version: current.form?.version || null,
              response_id: response?.data?.data?.response_id || null,
              answers,
              captured_at: completedAt,
            },
            risk_assessment: {
              ...current.risk_assessment,
              status: "completed",
              completed_at: completedAt,
            },
          };
        });
        setSuccess("Risk assessment completed.");
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Unable to complete this risk assessment right now."
        );
      } finally {
        setSubmitting(false);
        isSubmittingRef.current = false;
      }
    };

    surveyModel.onUploadFiles.add(uploadFiles);
    surveyModel.onCompleting.add(completing);

    return () => {
      surveyModel.onUploadFiles.remove(uploadFiles);
      surveyModel.onCompleting.remove(completing);
    };
  }, [surveyModel, uploadFiles, uploadPrefix, params.id, riskData?.can_submit]);

  const downloadCompletedFiles = async () => {
    if (!displayFiles.length) return;

    const zip = new JSZip();
    const usedNames = new Map();

    const ensureUniqueName = (name) => {
      if (!usedNames.has(name)) {
        usedNames.set(name, 1);
        return name;
      }

      const count = usedNames.get(name);
      const parts = name.split(".");
      const ext = parts.length > 1 ? parts.pop() : "";
      const base = parts.join(".");
      let candidate;
      let i = count;
      do {
        candidate = ext ? `${base}(${i}).${ext}` : `${base}(${i})`;
        i += 1;
      } while (usedNames.has(candidate));

      usedNames.set(name, i);
      usedNames.set(candidate, 1);
      return candidate;
    };

    displayFiles.forEach((file) => {
      const base64 = dataUrlToBase64(file.content || file.data);
      if (!base64) return;

      const rawName = file.name || file.filename || file.key || "file";
      zip.file(ensureUniqueName(rawName), base64, { base64: true });
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${params.id}-risk-assessment-files.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const createPublicLink = async () => {
    setCreatingPublicLink(true);
    setPublicLinkError(null);

    try {
      const response = await Axios.post(
        `/api/risk-assessment/${params.id}/public-link`
      );
      setPublicLinkData(response.data?.data || null);
    } catch (err) {
      setPublicLinkError(
        err?.response?.data?.message ||
          "Unable to create a public risk assessment link right now."
      );
    } finally {
      setCreatingPublicLink(false);
    }
  };

  if (loading || submitting || hydratingFiles) {
    return <Loader />;
  }

  const verificationInfo = riskData?.verification || null;
  const navItems = buildNavItems(params.id, verificationInfo);
  const submittedAt = riskAssessment?.completed_at || riskData?.submission?.captured_at;

  return (
    <>
      <SubNav items={navItems} />
      <Card title="Tranche 2 Risk Assessment">
        {error && <Message type="warning">{error}</Message>}
        {success && <Message type="success">{success}</Message>}
        {fileLoadError && <Message type="warning">{fileLoadError}</Message>}
        {publicLinkError && <Message type="warning">{publicLinkError}</Message>}

        {verificationInfo && (
          <div className="space-y-1 pb-4">
            <p>
              Verification: {verificationInfo.first_name || "Unknown"}{" "}
              {verificationInfo.last_name || ""}
            </p>
            <p>Email: {verificationInfo.email || "No email"}</p>
            <p>Biometric result: {verificationInfo.result || "Pending"}</p>
            <p>Status: {riskAssessment?.status || "unknown"}</p>
          </div>
        )}

        {riskAssessment?.edd_active === true && (
          <div className="space-y-2 pb-2">
            <Message type="warning">
              Enhanced Due Diligence (EDD) is active for this customer.
            </Message>
            {riskData?.edd_case_id && (
              <Button
                small
                text="Open EDD case"
                goto={`/verification/edd/${riskData.edd_case_id}`}
              />
            )}
          </div>
        )}

        {!riskData && !error && (
          <Message type="warning">Risk assessment data is unavailable.</Message>
        )}

        {riskData?.can_submit !== true && riskAssessment?.status !== "completed" && (
          <Message>
            This risk assessment is not ready for logged-in completion yet.
          </Message>
        )}

        {riskAssessment?.status === "completed" && (
          <Message type="success">
            This risk assessment was completed
            {submittedAt ? ` at ${new Date(submittedAt).toLocaleString()}` : ""}.
          </Message>
        )}

        {isCompleted && (scoringSummary || scoringRating) && (
          <div className="space-y-3 pb-4">
            <ScoringSummary scoring={scoringSummary} rating={scoringRating} />
            {scoringResponseId && (
              <ScoreOverrideControl
                responseId={scoringResponseId}
                scoring={scoringSummary}
                onUpdated={(data) =>
                  setRiskData((current) =>
                    current
                      ? {
                          ...current,
                          scoring: data?.scoring ?? current.scoring,
                          rating:
                            data?.effective_rating !== undefined
                              ? data.effective_rating
                              : current.rating,
                        }
                      : current
                  )
                }
              />
            )}
          </div>
        )}

        {riskData?.can_submit === true && (
          <div className="space-y-3 pb-4">
            <Message>
              Generate a PIN-protected public review link if the assessor needs
              to complete this assessment without signing into the app.
            </Message>

            <Button
              text={
                creatingPublicLink
                  ? "Creating Public Link..."
                  : "Create Public Review Link"
              }
              action={createPublicLink}
            />

            {publicLinkData?.url && (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-700">
                  Expires:{" "}
                  {publicLinkData?.expires_at
                    ? new Date(publicLinkData.expires_at).toLocaleString()
                    : "Unknown"}
                </p>
                <input
                  readOnly
                  value={publicLinkData.url}
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                />
                <Button
                  text="Copy Public Link"
                  action={() => {
                    navigator.clipboard.writeText(publicLinkData.url);
                    context.notification.show("Copied!", "success", true);
                  }}
                />
              </div>
            )}
          </div>
        )}

        {isCompleted && !surveyModel && (
          <Message type="warning">
            The completed risk-assessment form could not be loaded for review.
          </Message>
        )}

        {riskData?.can_submit === true && !surveyModel && (
          <Message type="warning">
            The configured risk-assessment form is unavailable.
          </Message>
        )}

        {isCompleted && displayFiles.length > 0 && (
          <div className="pb-4">
            <Button text="Download Files" action={downloadCompletedFiles} />
          </div>
        )}

        {(riskData?.can_submit === true || isCompleted) && surveyModel && (
          <div className="overflow-x-auto">
            <Survey model={surveyModel} />
          </div>
        )}

        <div className="pt-4">
          <Button text="Back to verification" goto={`/verification/${params.id}`} />
        </div>
      </Card>
    </>
  );
}
